/* eslint-disable @typescript-eslint/no-explicit-any */
// FIXME: no any
import MultipleSelector, {
  MultipleSelectorRef,
  Option,
} from '@/components/ui/multiple-selector';
import { Skeleton } from '@/components/ui/skeleton';
import usePromise from '@/hooks/usePromise';
import {
  entityConfigs,
  EntityIdMap,
  EntityType,
} from '@/lib/entities/entityConfig';
import { useCallback, useMemo } from 'react';
import { FieldComponentProps } from './types';
import { useScrollIntoViewOnFocus } from '@/hooks/useScrollIntoViewOnFocus';

// Type guards for User and Label
function isUser(entity: any): entity is import('@certhon/models').User {
  return entity && typeof entity.loginname === 'string';
}
function isLabel(entity: any): entity is import('@certhon/models').Label {
  return entity && typeof entity.name === 'string' && 'color' in entity;
}

// Individual entity token component to isolate hook calls
function EntityToken<K extends EntityType>({
  entityType,
  entityId,
}: {
  entityType: K;
  entityId: EntityIdMap[K];
}) {
  const entityConfig = entityConfigs[entityType];
  const entity = usePromise(
    useMemo(
      () => entityConfig.getById(String(entityId)),
      [entityId, entityConfig],
    ) as Promise<any>,
  );

  if (!entity) {
    return <Skeleton className="h-4 w-20" />;
  }

  if (entityType === 'user' && isUser(entity)) {
    return entityConfigs.user.renderBadge(entity as any);
  }

  if (entityType === 'label' && isLabel(entity)) {
    return entityConfigs.label.renderBadge(entity as any);
  }

  return null;
}

// View component: show tokens for all selected entities
function FieldView<K extends EntityType>({
  entityType,
  entityIds,
  placeholder = 'No selection',
}: {
  entityType: K;
  entityIds: EntityIdMap[K][];
  placeholder?: string;
}) {
  if (!entityIds.length) {
    return <p className="text-muted-foreground">{placeholder}</p>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {entityIds.map((entityId) => (
        <EntityToken
          key={`${entityType}-${entityId}`}
          entityType={entityType}
          entityId={entityId}
        />
      ))}
    </div>
  );
}

// Edit component: use MultipleSelector for multi-entity selection
function FieldEdit<K extends EntityType>({
  entityType,
  value,
  onChange,
  placeholder,
}: {
  entityType: K;
  value: EntityIdMap[K][];
  onChange: (value: EntityIdMap[K][]) => void;
  placeholder?: string;
}) {
  const inputRef = useScrollIntoViewOnFocus<HTMLInputElement>({
    bufferPx: 400,
  });

  // Callback ref to extract the input element from MultipleSelectorRef
  const handleMultipleSelectorRef = useCallback(
    (ref: MultipleSelectorRef | null) => {
      if (ref?.input) {
        inputRef.current = ref.input;
      }
    },
    [inputRef],
  );

  const entityConfig = entityConfigs[entityType];

  // Use usePromise with search function
  const searchResults =
    usePromise(
      useMemo(() => entityConfig.search(''), [entityConfig]) as Promise<any[]>,
    ) || [];

  // For MultipleSelector, label must be string. We'll use name or loginname.
  const options = searchResults.map((entity: any) => ({
    value: String(entityConfig.getOptionValue(entity)),
    data: entity,
    label: entityConfig.getOptionLabel(entity),
    keywords: entityConfig
      .getOptionKeywords(entity)
      .map((k: unknown) => String(k)),
  }));

  // Map selected IDs to Option objects
  const selectedOptions = useMemo(() => {
    return value
      .map((val) => options.find((opt: any) => opt.value == val))
      .filter(Boolean) as Option<unknown>[];
  }, [options, value]);

  // When selection changes, update the array of IDs
  const handleChange = (opts: Option[]) => {
    onChange(opts.map((opt) => Number(opt.value)));
  };

  return (
    <MultipleSelector
      ref={handleMultipleSelectorRef}
      value={selectedOptions}
      options={options}
      onChange={handleChange}
      placeholder={placeholder || `Select ${entityType}s`}
      loadingIndicator={<Skeleton className="h-8 w-full" />}
      emptyIndicator={<span>No {entityType}s found.</span>}
      className="min-h-8 w-full text-sm"
      commandProps={{
        filter: (value, search, keywords) => {
          if (!search) return 1;
          const needle = search.toLowerCase();
          const haystack = [value, ...(keywords ?? [])].join(' ').toLowerCase();
          return haystack.includes(needle) ? 1 : 0;
        },
      }}
      renderItem={({ option, onUnselect }) =>
        entityConfig.renderBadge(option.data as any, onUnselect)
      }
    />
  );
}

export function ForeignKeyArrayField({
  formMode,
  controllerField,
  field,
}: FieldComponentProps) {
  const entityType = field.meta.entity as EntityType;

  if (!entityType) {
    return (
      <div className="text-destructive bg-destructive/10 rounded-md p-2 text-sm">
        ForeignKeyArray field "{field.name}" is missing entity type "
        {entityType}"
      </div>
    );
  }

  if (formMode === 'loading') {
    return <Skeleton className="h-8 w-full" />;
  }

  if (formMode === 'edit') {
    return (
      <FieldEdit
        entityType={entityType}
        value={controllerField.value || []}
        onChange={controllerField.onChange}
        placeholder={field.meta.placeholder || `Select ${entityType}s`}
      />
    );
  }

  return (
    <FieldView
      entityType={entityType}
      entityIds={controllerField.value || []}
      placeholder={field.meta.placeholder || 'No selection'}
    />
  );
}
